import { ChordChart } from '@revelio/replots';

import { usePrimaryFilterState } from '../../../filters/hooks/usePrimaryFilterState';
import { usePlotMetric } from '../../../hooks/usePlotMetric';
import type { TransitionBreakdownData } from '../../DataProvider/types';
import {
  computeChordEmptyStateSuggestions,
  convertTransitionDataToChordMatrix,
} from '../../DataProvider/utils/convert-transition-data-to-chord';
import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import type { TransitionMetricValues } from '../../PageConfigProvider/types/metric';
import { useSafeChart } from '../ChartProvider';

const DEFAULT_CHORD_LABEL_FONT_SIZE = 14;

type ChordProps = {
  data: TransitionBreakdownData[];
};

export const Chord = ({ data }: ChordProps) => {
  const metric = usePlotMetric();
  const { primaryFilter: tabContentPrimaryFilter } = useTabContentConfig();
  const { primaryFilter } = usePrimaryFilterState(tabContentPrimaryFilter?.id);
  const { item } = useSafeChart();

  const selectedEntities =
    primaryFilter?.primarySelections?.map((selection) => ({
      id: selection.id,
      name: selection.name,
    })) ??
    data.map((entityRow) => ({
      id: entityRow.id ?? undefined,
      name: entityRow.dim,
    }));

  const transitionMetric: TransitionMetricValues = (() => {
    if (metric === 'transitions_inflow') return 'transitions_inflow';
    if (metric === 'transitions_inflows_and_outflows')
      return 'transitions_inflows_and_outflows';
    return 'transitions_outflow';
  })();

  const chordData = convertTransitionDataToChordMatrix({
    data,
    selectedEntities,
    metric: transitionMetric,
  });

  const emptyStateSuggestions = computeChordEmptyStateSuggestions({
    data,
    selectedEntities,
  });

  const chordOptions = item?.plot?.chord;

  return (
    <ChordChart
      data={chordData}
      labelFontSize={item?.plot?.fontSize ?? DEFAULT_CHORD_LABEL_FONT_SIZE}
      showTickLabels={chordOptions?.showTickLabels ?? false}
      tickLabelMode={chordOptions?.tickLabelMode ?? 'count'}
      emptyStateSuggestions={emptyStateSuggestions}
    />
  );
};
